import {
  CONDITION_STATUS,
  DESTINATION,
  EMQ_CONDITION_TYPE,
  EMQ_TREND,
  EmqHealthCondition,
} from '@adtrackify/at-tracking-event-types';
import { useMemo } from 'react';

import { usePixelDestinations } from '@/hooks/account/use-pixel-destinations';
import type {
  UseEmqPermissionErrorResult,
} from '../types/use-emq-health-status-types';
import { EMQ_AGGREGATED_STATUS } from '../types/use-emq-health-status-enums';
type ApiEmqHealthStatus = 'healthy' | 'warning' | 'critical' | 'unknown';

interface EmqHealthStatusData {
  overallScore: number | null;
  status: ApiEmqHealthStatus;
  lastCheckedAt: string;
  eventScores: Record<string, number>;
  eventCoverage: number | null;
  dedupeCoverage: number | null;
  criticalDiagnosticsCount: number;
  previousScore: number | null;
  scoreChange: number | null;
  scoreTrend: EMQ_TREND | null;
}

interface UseEmqHealthStatusResult {
  emqHealth: EmqHealthStatusData | null;
  status: EMQ_AGGREGATED_STATUS;
  isLoading: boolean;
  isConfigured: boolean;
}

export function useEmqHealthStatus(): UseEmqHealthStatusResult {
  const destinations = usePixelDestinations();

  const result = useMemo(() => {
    // If destinations haven't been loaded yet, return unknown
    if (destinations.length === 0) {
      return {
        emqHealth: null,
        status: EMQ_AGGREGATED_STATUS.UNKNOWN,
        isLoading: false,
        isConfigured: false,
      };
    }
    
    // Find the Facebook destination (the one that has EMQ data)
    const facebookDestination = destinations.find(
      dest => dest.destination === DESTINATION.FACEBOOK && dest.enabled
    );

    // If no Facebook destination, EMQ is not configured
    if (!facebookDestination) {
      return {
        emqHealth: null,
        status: EMQ_AGGREGATED_STATUS.NOT_CONFIGURED,
        isLoading: false,
        isConfigured: false,
      };
    }

    const emqHealth = facebookDestination.emqHealthStatus;

    // If no EMQ health status yet, return unknown
    if (!emqHealth) {
      return {
        emqHealth: null,
        status: EMQ_AGGREGATED_STATUS.UNKNOWN,
        isLoading: false,
        isConfigured: true,
      };
    }

    // Map status string to our aggregated status
    const validStatuses: EMQ_AGGREGATED_STATUS[] = [
      EMQ_AGGREGATED_STATUS.HEALTHY,
      EMQ_AGGREGATED_STATUS.WARNING,
      EMQ_AGGREGATED_STATUS.CRITICAL,
    ];
    const status: EMQ_AGGREGATED_STATUS = validStatuses.includes(emqHealth.status as unknown as EMQ_AGGREGATED_STATUS)
      ? (emqHealth.status as unknown as EMQ_AGGREGATED_STATUS)
      : EMQ_AGGREGATED_STATUS.UNKNOWN;

    return {
      emqHealth: emqHealth as unknown as EmqHealthStatusData,
      status,
      isLoading: false,
      isConfigured: true,
    };
  }, [destinations]);

  return result;
}

export function useEmqPermissionError(): UseEmqPermissionErrorResult {
  const destinations = usePixelDestinations();

  return useMemo(() => {
    const facebookDestination = destinations.find(
      dest => dest.destination === DESTINATION.FACEBOOK && dest.enabled
    );

    const lastError = (
      facebookDestination?.emqHealthStatus as unknown as {
        lastError?: { errorCode?: number; errorType?: string; displayMessage?: string; errorMessage?: string };
      } | undefined
    )?.lastError;

    if (lastError) {
      return {
        hasPermissionError: true,
        isPermissionFailure: lastError.errorType === 'OAuthException',
        message: lastError.displayMessage || lastError.errorMessage || null,
      };
    }

    const conditions = (facebookDestination?.emqHealthStatus?.conditions ??
      facebookDestination?.emqHealthStatus?.activeConditions ??
      []) as EmqHealthCondition[];

    const permissionCondition = conditions.find(
      condition =>
        condition.type === EMQ_CONDITION_TYPE.INSUFFICIENT_DATASET_QUALITY_PERMISSIONS &&
        (!condition.status || condition.status === CONDITION_STATUS.ACTIVE)
    );

    return {
      hasPermissionError: Boolean(permissionCondition),
      isPermissionFailure: Boolean(permissionCondition),
      message: permissionCondition?.message ?? null,
    };
  }, [destinations]);
}
